import { useMemo, useState, type ReactNode } from "react";
import {
  ChartPanel,
  DashButton,
  DataSurface,
  GamesPanel,
  ShareBars,
  StatusPill,
  AlertIcon,
  CheckIcon,
  ChevronIcon,
  ChildrenIcon,
  PlayIcon,
  StickerIcon,
  TargetIcon,
  TrendUpIcon,
  Pagination,
  usePagination,
  /* ⚠️ FROM `shared/components`, NOT `./TeacherUI` — that shim says so itself:
     *"new work imports from `shared/components`"*. It re-exports `StatCard` for
     the screens that already asked for it and has never carried `StatGrid`. */
  StatCard,
  StatGrid,
  type StatDelta,
  RewardTile,
  ClockIcon,
  PointIcon,
  /* The de-emoji set and the shapes that replaced the hand-drawn panels. */
  Avatar,
  CalendarIcon,
  CoinIcon,
  DownloadIcon,
  FlameIcon,
  GamepadIcon,
  MedalIcon,
  PhoneIcon,
  PriorityCard,
  RankBadge,
  ReportsIcon,
  SearchIcon,
  skillTint,
} from "../../../shared/components";
import type { Skill } from "../../../shared/types/prisma-enums";
/* 🔴 THE CANONICAL GAME PLATES — replacing this file's own emoji table, which
   disagreed with the curriculum's. See the note where `GAME_ICONS` was. */
import { GAME_ART_BY_ID, type GameArt } from "../../../shared/ui/gameArt";
import type { GameId } from "../../../shared/curriculum";
import { getClassGames, type ClassGames } from "../../../shared/api/games";
import { GAMES_PANEL_COPY } from "../../../shared/ui/gamesCopy";
import { useResource, type Resource } from "../../../shared/hooks/useResource";
import {
  getClassOverview,
  getCoverage,
  getInsight,
  getLeaderboard,
  getPlayerEvents,
  getRoster,
  getClassActivity,
  getClassSkills,
  type ClassActivity,
  type ClassOverview,
  type ClassSkills,
  type EventRow,
  type Insight,
  type LeaderboardRow,
  type NcfCoverage,
  type RosterRow,
  type StaffAuth,
} from "../../../shared/api/teaching";
import { TEACHER_COPY, describeError, type Lang } from "./copy";
/* 🔴 THE STAT ROW'S ARITHMETIC, PURE AND TESTED. Six tiles' worth of division,
   subtraction and rounding inline in this file is six edges no test can reach
   without a browser — and each one is wrong in a way that still renders. */
import {
  ACCURACY_FLOOR,
  metricsOf,
  type Change,
} from "./overviewMetrics";
import { launchConfirmLine } from "./teacherFlow";
import {
  PageHead,
  Panel,
  SectionHead,
  TableCard,
  Td,
  Thead,
  Tr,
} from "./TeacherUI";
import { AddStudentForm } from "./RosterControls";
import { ClassActivityChart } from "./ClassActivityChart";
/* 🔴 `SKILL_LABEL` — the enum→English table this tier already owns. The
   leaderboard was printing `GENERAL_AWARENESS` at teachers; see the chip. */
import { SKILL_LABEL, STUDENT_COPY } from "./studentCopy";
import { describeLastPlayed } from "./lastPlayed";

/**
 * D1–D7.
 *
 * 🔴 EVERY SCREEN BELOW ISSUES ITS OWN REAL REQUEST. None of them takes data as
 * a prop, and there is no fixture file in this directory — that is the whole
 * discipline of §8.4, whose TODO row reads *"the predecessor rendered 8
 * hardcoded children while working endpoints sat uncalled."* A component taking
 * `rows` with a fixture beside it is the same failure moved up one level.
 *
 * ⚠️ They all 401 today, because staff login is Unit 9 (OD-51). The error
 * surface says so by name rather than "couldn't load".
 */

interface Base {
  lang?: Lang;
  auth?: StaffAuth;
}

/** The four states, wired to `DataSurface`'s contract in one place. */
function Surface<T>({
  resource,
  lang,
  children,
  emptyMessage,
  emptyActionLabel,
  onEmptyAction,
  skeletonRows = 4,
}: {
  resource: Resource<T>;
  lang: Lang;
  children: (data: T) => ReactNode;
  emptyMessage?: ReactNode;
  emptyActionLabel?: ReactNode;
  onEmptyAction?: () => void;
  skeletonRows?: number;
}) {
  const t = TEACHER_COPY[lang];
  return (
    <DataSurface
      state={resource.state}
      skeletonRows={skeletonRows}
      emptyMessage={emptyMessage ?? t.emptyClass}
      emptyActionLabel={emptyActionLabel}
      onEmptyAction={onEmptyAction}
      errorMessage={describeError(resource.error, lang)}
      /* 🔴 "When it last worked" — DataSurface calls this the clause that gets
         dropped, and the one that tells a teacher whether to panic. */
      errorLastWorked={
        resource.lastLoadedAt
          ? t.lastWorked(resource.lastLoadedAt.toLocaleTimeString())
          : t.neverWorked
      }
      errorRetryLabel={t.retry}
      onRetry={resource.retry}
    >
      {resource.data === null ? null : children(resource.data)}
    </DataSurface>
  );
}

/*
 * ⚠️ `Scroller` IS GONE — `TableCard` absorbed it. It wrapped tables in
 * `overflow-x-auto` for the reason that still holds (these are the tier's
 * densest surface and a long name overruns them), but every caller then had
 * to remember to add a border and a radius separately, and most did not. One
 * component that does both is one fewer thing to forget.
 */

/* ─────────────────────────────────────────────────────────────── D1 ── */

export interface ModeSelectProps extends Base {
  teacherName: string;
  className: string;
  onPlay: () => void;
}

/**
 * 🔴 THE FORK THAT PROTECTS THE 30-MINUTE SLOT.
 */
export function ModeSelect({
  teacherName,
  className,
  lang = "en",
  onPlay,
}: ModeSelectProps) {
  const t = TEACHER_COPY[lang];

  const cards = [
    {
      icon: <PlayIcon size={24} strokeWidth={2.4} />,
      chip: "bg-primary",
      title: t.playTitle,
      body: t.playBody,
      action: t.playAction,
      on: onPlay,
      testId: "mode-play",
    },
  ];

  return (
    <section className="font-dash text-ink">
      <PageHead
        title={t.welcomeTitle(teacherName)}
        subtitle={t.modeHint(className)}
      />

      <div className="max-w-xl">
        {cards.map((card) => (
          <button
            key={card.title}
            type="button"
            data-testid={card.testId}
            onClick={card.on}
            className={`group flex min-h-[13.125rem] w-full flex-col items-start rounded-lg border border-line bg-surface p-7 text-left shadow-card transition-transform duration-200 ease-dash hover:-translate-y-1`}
          >
            <span
              aria-hidden="true"
              className={`flex h-12 w-12 items-center justify-center rounded-lg text-primary-ink ${card.chip}`}
            >
              {card.icon}
            </span>

            <span className="mt-4 text-dash-lg font-extrabold">
              {card.title}
            </span>
            <span className="mt-1.5 text-dash-sm text-muted">{card.body}</span>

            <span className="mt-auto inline-flex h-[2.625rem] items-center rounded-md bg-primary px-5 pt-0.5 text-dash-sm font-bold text-primary-ink transition-colors duration-150 ease-dash group-hover:bg-primary-deep">
              {card.action}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────── D2 ── */

/* ─────────────────────────────────────────────────────────────── D2 ── */

export interface RosterScreenProps extends Base {
  classId: string;
  onPick: (playerId: string) => void;
}

function formatRollNumber(roll: string | null): string {
  if (!roll) return "—";
  const num = parseInt(roll, 10);
  if (!isNaN(num) && num < 10 && num > 0) {
    return `0${num}`;
  }
  return roll;
}

/*
 * ✅ `getStudentAvatarIcon` IS GONE, AND IT IS THE WORST THING THIS FILE
 * CONTAINED.
 *
 * It was seven lines long and it read:
 *
 *     if (lower.includes('amaira') || lower.includes('zara') || …) return '👧'
 *     return '👦'
 *
 * So the product looked at a four-year-old's first name, matched it against a
 * list of seven, and drew an assertion about that child's gender beside their
 * row — on the register a teacher reads out, in the parent's own school. It was
 * wrong for every child outside the list by construction, and unknowable for
 * most of the ones inside it. A name does not carry a gender, and no amount of
 * extending the list would have made it do so.
 *
 * `Avatar` (shared kit) is the replacement: initials in a tinted disc, with the
 * tone derived from the whole name so one child keeps one colour down the list
 * and across reloads. It is the same visual anchor, costs nothing to get right,
 * and cannot be wrong about a person. The school tier had already solved this
 * slot exactly this way — its own note records the same objection — so this is
 * the teacher tier catching up rather than a new idea.
 */

/** *"The linkage made visible"* — each row carries the roster import's output. */
export function RosterScreen({
  classId,
  lang = "en",
  auth,
  onPick,
}: RosterScreenProps) {
  const t = TEACHER_COPY[lang];
  const [query, setQuery] = useState("");
  const resource = useResource<RosterRow[]>(
    (signal) => getRoster(classId, { ...auth, signal }),
    [classId],
    { isEmpty: (rows) => rows.length === 0 },
  );

  const rows = useMemo(() => {
    const data = resource.data ?? [];
    const q = query.trim().toLowerCase();
    if (q === "") return data;
    // Name or roll — a teacher knows one or the other, never reliably both.
    return data.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        (r.rollNumber ?? "").toLowerCase().includes(q),
    );
  }, [resource.data, query]);

  return (
    <section className="font-dash text-dash-sm text-ink max-w-5xl mx-auto pb-12">
      {/* Page Header with Icon & Search */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-start gap-4">
          {/* 🔴 THE ICON SET, NOT 👥. §6 reserves emoji for game identity, and a
              register heading is as far from that as this tier gets. */}
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-line bg-primary-tint text-primary shadow-sm">
            <ChildrenIcon size={22} strokeWidth={2.4} />
          </div>
          <div>
            <h1 className="font-dash text-2xl font-extrabold text-ink">
              {t.roster}
            </h1>
            <p className="mt-0.5 font-dash text-dash-xs font-bold text-muted">
              {t.rosterHint}
            </p>
          </div>
        </div>

        {/* Search Input Box */}
        <div className="w-full sm:w-[20rem] relative">
          {/* ⚠️ `SearchIcon`, NOT 🔍 — and the magnifier is the emoji with the
              widest rendering spread of any on these screens: it is a different
              angle, weight and colour on Windows 10, Windows 11 and ChromeOS,
              which is three different-looking search boxes for one product. */}
          <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted">
            <SearchIcon size={16} />
          </span>
          <input
            className="w-full rounded-xl border border-line bg-surface py-2.5 pr-4 pl-9 font-dash text-dash-xs font-medium text-ink shadow-sm outline-none transition-all focus:border-primary focus:ring-2 focus:ring-primary-tint"
            placeholder={t.search}
            aria-label={t.search}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      <Surface resource={resource} lang={lang} skeletonRows={6}>
        {() =>
          rows.length === 0 ? (
            <Panel>
              <p className="text-muted font-bold">{t.noMatch}</p>
            </Panel>
          ) : (
            <>
              {/* Roster Table Card */}
              <div className="rounded-2xl border border-line bg-surface overflow-hidden shadow-card">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-left font-dash text-dash-xs">
                    <thead>
                      <tr className="bg-[#fff5f8] border-b border-line text-primary font-extrabold">
                        <th className="px-6 py-4">{t.roll}</th>
                        <th className="px-6 py-4">{t.student}</th>
                        <th className="px-6 py-4">{t.parent}</th>
                        <th className="px-6 py-4">{t.phone}</th>
                        <th className="px-6 py-4">{t.lastPlayed}</th>
                        <th className="px-6 py-4 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line/60">
                      {rows.map((r) => {
                        const rollFormatted = formatRollNumber(r.rollNumber);
                        const lastPlayed = describeLastPlayed(
                          r.lastPlayed,
                          t.never,
                        );

                        return (
                          <tr
                            key={r.playerId}
                            className="hover:bg-canvas/50 transition-colors"
                          >
                            {/* Roll Number Pill */}
                            <td className="px-6 py-4 font-extrabold">
                              <span className="inline-flex items-center justify-center rounded-full bg-primary-tint text-primary px-3 py-1 text-dash-xs font-extrabold border border-pink-100 shadow-xs min-w-[2.125rem]">
                                {rollFormatted}
                              </span>
                            </td>

                            {/* Student Name & Avatar */}
                            <td className="px-6 py-4 font-extrabold text-ink">
                              <div className="flex items-center gap-3">
                                {/* 🔴 INITIALS, NOT A GUESS AT THE CHILD'S
                                    GENDER — see the note where
                                    `getStudentAvatarIcon` used to be. */}
                                <Avatar name={r.name} size={40} />
                                <span className="font-dash text-dash-sm font-extrabold text-ink">
                                  {r.name}
                                </span>
                              </div>
                            </td>

                            {/*
                              Parent · Phone · Last played.

                              ⚠️ THE THREE DISCS WERE `bg-[#fff7ed] text-[#f97316]`,
                              `bg-[#f5f3ff] text-[#8b5cf6]` and `bg-[#fff1f2]
                              text-[#f43f5e]` — six raw hex values, none of them a
                              Kalqy token, on one table row. They are tokens now,
                              so the row moves when the theme does.

                              ⚠️ AND THE EMPTY SECOND LINE UNDER EACH IS GONE. It
                              rendered a bare em dash under every parent, every
                              phone and every date — a column of placeholders for
                              a field that does not exist, which reads as data
                              that failed to load.
                            */}
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-line bg-canvas text-muted">
                                  <ChildrenIcon size={15} />
                                </span>
                                <span className="font-dash text-dash-xs font-bold text-ink">
                                  {r.parentName ?? t.never}
                                </span>
                              </div>
                            </td>

                            {/* Phone Column */}
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-line bg-canvas text-muted">
                                  <PhoneIcon size={15} />
                                </span>
                                <span className="font-dash text-dash-xs font-bold text-ink">
                                  {r.parentPhone ?? t.never}
                                </span>
                              </div>
                            </td>

                            {/* Last Played Column */}
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-line bg-canvas text-muted">
                                  <CalendarIcon size={15} />
                                </span>
                                <div>
                                  <div className="font-dash font-bold text-dash-xs text-ink">
                                    {lastPlayed.date}
                                  </div>
                                  <div className="font-dash text-[11px] font-bold text-muted">
                                    {lastPlayed.relative || "—"}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Action Button */}
                            <td className="px-6 py-4 text-right">
                              <button
                                type="button"
                                onClick={() => onPick(r.playerId)}
                                className="inline-flex items-center gap-2 rounded-xl bg-primary hover:bg-[#c90059] active:scale-95 text-white font-dash font-bold text-dash-xs px-5 py-2.5 shadow-sm transition-all cursor-pointer"
                              >
                                <PlayIcon size={16} strokeWidth={2.4} />
                                <span>{t.play}</span>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Bottom How Play Mode Works Banner */}
              <div className="mt-8 rounded-2xl border border-[#dbeafe] bg-gradient-to-r from-[#eff6ff] to-[#f0f9ff] p-4 sm:p-6 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#dbeafe] text-[#2563eb] text-xl border border-blue-100 shadow-xs">
                    <PlayIcon size={24} strokeWidth={2.4} />
                  </div>
                  <div>
                    <h3 className="font-dash text-dash-md font-extrabold text-[#1e3a8a]">
                      How play mode works?
                    </h3>
                    <p className="mt-1 font-dash text-dash-xs font-bold text-[#3b82f6]">
                      Select a student and click Play. You'll be asked to
                      confirm before the game starts.
                    </p>
                  </div>
                </div>

                {/* Gaming Console Illustration Graphic */}
                <div className="relative shrink-0 select-none pointer-events-none">
                  <svg className="w-36 h-24" viewBox="0 0 160 100" fill="none">
                    {/* Sparkle stars */}
                    <path
                      d="M15 20 L17 15 L22 13 L17 11 L15 6 L13 11 L8 13 L13 15 Z"
                      fill="#facc15"
                    />
                    <path
                      d="M145 75 L146 71 L150 70 L146 69 L145 65 L144 69 L140 71 Z"
                      fill="#facc15"
                    />
                    <path
                      d="M130 15 L131 12 L134 11 L131 10 L130 7 L129 10 L126 11 L129 12 Z"
                      fill="#facc15"
                      opacity="0.7"
                    />

                    {/* Handheld Console Body */}
                    <rect
                      x="25"
                      y="25"
                      width="110"
                      height="60"
                      rx="20"
                      fill="#3b82f6"
                      stroke="#1d4ed8"
                      strokeWidth="3"
                    />
                    <path
                      d="M25 45 C25 34 34 25 45 25 L50 25 V85 L45 85 C34 85 25 76 25 65 Z"
                      fill="#2563eb"
                    />
                    <path
                      d="M135 45 C135 34 126 25 115 25 L110 25 V85 L115 85 C126 85 135 76 135 65 Z"
                      fill="#2563eb"
                    />

                    {/* Screen */}
                    <rect
                      x="52"
                      y="32"
                      width="56"
                      height="46"
                      rx="8"
                      fill="#e0f2fe"
                      stroke="#93c5fd"
                      strokeWidth="2"
                    />
                    <circle cx="70" cy="52" r="3" fill="#1e3a8a" />
                    <circle cx="90" cy="52" r="3" fill="#1e3a8a" />
                    <path
                      d="M76 58 Q80 62 84 58"
                      stroke="#1e3a8a"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      fill="none"
                    />
                    <circle
                      cx="64"
                      cy="55"
                      r="2.5"
                      fill="#f472b6"
                      opacity="0.6"
                    />
                    <circle
                      cx="96"
                      cy="55"
                      r="2.5"
                      fill="#f472b6"
                      opacity="0.6"
                    />

                    {/* Controls */}
                    <rect
                      x="33"
                      y="48"
                      width="12"
                      height="4"
                      rx="1"
                      fill="#eff6ff"
                    />
                    <rect
                      x="37"
                      y="44"
                      width="4"
                      height="12"
                      rx="1"
                      fill="#eff6ff"
                    />
                    <circle cx="120" cy="48" r="3" fill="#ec4899" />
                    <circle cx="126" cy="54" r="3" fill="#facc15" />
                    <circle cx="120" cy="60" r="3" fill="#4ade80" />
                    <circle cx="114" cy="54" r="3" fill="#60a5fa" />

                    {/* Heart Bubble */}
                    <g transform="translate(122, 5)">
                      <rect
                        x="0"
                        y="0"
                        width="24"
                        height="20"
                        rx="6"
                        fill="#f43f5e"
                      />
                      <polygon points="6,20 12,25 14,20" fill="#f43f5e" />
                      <path
                        d="M12 13 C12 13 7 9 7 6.5 C7 5 8.2 4 9.5 4 C10.5 4 11.5 4.8 12 5.5 C12.5 4.8 13.5 4 14.5 4 C15.8 4 17 5 17 6.5 C17 9 12 13 12 13 Z"
                        fill="white"
                      />
                    </g>
                  </svg>
                </div>
              </div>
            </>
          )
        }
      </Surface>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────── D3 ── */

export interface LaunchConfirmProps extends Base {
  classId: string;
  playerId: string;
  games: readonly { id: string; title: string }[];
  onLaunch: (gameId: string) => void;
  onBack: () => void;
}

/**
 * 🔴 THE SCREEN THAT STOPS A WHOLE SESSION LANDING ON THE WRONG CHILD.
 * *"Cheap to build, expensive to omit"* (wireframes D3).
 */
export function LaunchConfirm({
  classId,
  playerId,
  games,
  lang = "en",
  auth,
  onLaunch,
  onBack,
}: LaunchConfirmProps) {
  const t = TEACHER_COPY[lang];
  const [gameId, setGameId] = useState(games[0]?.id ?? "");
  const resource = useResource<RosterRow[]>(
    (signal) => getRoster(classId, { ...auth, signal }),
    [classId],
  );
  const child = resource.data?.find((r) => r.playerId === playerId) ?? null;

  return (
    <section className="font-dash text-dash-sm text-ink max-w-3xl mx-auto pb-12">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="font-dash text-2xl font-extrabold text-ink">
            {t.confirmTitle}
          </h1>
          <p className="mt-1 font-dash text-dash-xs font-bold text-muted">
            {t.confirmHint}
          </p>
        </div>
        <button
          type="button"
          onClick={onBack}
          className="rounded-full border border-line bg-surface px-4 py-2 text-dash-xs font-extrabold text-ink-soft hover:bg-canvas transition-colors shadow-sm flex items-center gap-1.5 cursor-pointer"
        >
          <span>←</span> {t.back}
        </button>
      </div>

      <Surface resource={resource} lang={lang} skeletonRows={2}>
        {() =>
          !child ? (
            <Panel>
              <p className="text-muted font-bold">{t.noMatch}</p>
            </Panel>
          ) : (
            <div className="flex flex-col gap-6">
              {/* Selected Child Hero Confirmation Card */}
              <div className="rounded-2xl border-2 border-primary bg-gradient-to-r from-primary-tint to-primary-tint p-4 sm:p-6 shadow-md relative overflow-hidden">
                <div className="flex items-center gap-5">
                  {/* 🔴 INITIALS, NOT A GENDER GUESS — and this is the screen
                      where it mattered most: the confirmation a teacher reads
                      immediately before handing the laptop to that child. */}
                  <Avatar name={child.name} size={64} />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center rounded-pill border border-line bg-surface px-3 py-0.5 font-dash text-dash-xs font-extrabold text-primary shadow-xs">
                        {t.roll} {formatRollNumber(child.rollNumber)}
                      </span>
                      <span className="text-dash-xs font-bold text-primary">
                        ● Selected Child
                      </span>
                    </div>
                    <h2 className="mt-1 font-dash text-2xl font-extrabold text-ink">
                      {launchConfirmLine(child)}
                    </h2>
                    {child.parentName && (
                      <p className="mt-0.5 font-dash text-dash-xs font-bold text-muted">
                        Parent: {child.parentName}{" "}
                        {child.parentPhone ? `(${child.parentPhone})` : ""}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Pick Game Section */}
              <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card">
                <h3 className="font-dash text-dash-md font-extrabold text-ink mb-4 flex items-center gap-2">
                  <GamepadIcon size={18} /> {t.pickGame}
                </h3>

                {/*
                  🔴 FIVE CARDS THAT WERE THE SAME PICTURE AND THE SAME WORD.
                  Every card drew 🎮 — one identical glyph for all five games,
                  which is no help at all in a picker whose entire job is to
                  tell them apart — and `g.title` is the raw id here, because
                  the mount builds `GAMES.map(g => ({ id: g.id, title: g.id }))`.
                  So a teacher choosing a game for a four-year-old picked
                  between "sky-quest" and "letter-lagoon" under five identical
                  gamepads.

                  `GAME_ART_BY_ID` has the plate the child sees and the name a
                  parent would say. A teacher now recognises the game by the
                  same picture the child does, which is the point of a handover
                  screen.
                */}
                <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {games.map((g) => {
                    const isSelected = gameId === g.id;
                    const art = GAME_ART_BY_ID[g.id as GameId] as
                      | GameArt
                      | undefined;
                    return (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => setGameId(g.id)}
                        className={`flex cursor-pointer items-center gap-3 rounded-xl border p-4 text-left transition-all duration-150 ease-dash ${
                          isSelected
                            ? "border-primary bg-primary-tint shadow-card"
                            : "border-line bg-surface hover:bg-canvas"
                        }`}
                      >
                        <span
                          className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl border"
                          style={{
                            borderColor: art
                              ? `color-mix(in srgb, ${art.tint} 34%, var(--color-line))`
                              : "var(--color-line)",
                            background: art
                              ? `color-mix(in srgb, ${art.tint} 12%, var(--color-surface))`
                              : "var(--color-canvas)",
                          }}
                        >
                          {art ? (
                            <img
                              src={art.art}
                              alt=""
                              aria-hidden="true"
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <GamepadIcon size={18} />
                          )}
                        </span>
                        <div className="min-w-0">
                          <div className="truncate font-dash text-dash-sm font-extrabold text-ink">
                            {art ? art.title[lang] : g.title}
                          </div>
                          {/* ⚠️ THE SELECTED STATE IS SAID IN WORDS AS WELL AS
                              IN COLOUR (§12), and `aria-pressed` on the button
                              carries it to a screen reader. */}
                          <div className="font-dash text-[11px] font-bold text-muted">
                            {isSelected ? t.selected : t.tapToSelect}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="flex items-center gap-3 pt-4 border-t border-line">
                  <button
                    type="button"
                    disabled={gameId === ""}
                    onClick={() => onLaunch(gameId)}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary hover:bg-[#c90059] active:scale-95 disabled:opacity-50 text-white font-dash font-extrabold text-dash-sm px-6 py-3 shadow-md transition-all cursor-pointer"
                  >
                    <PlayIcon size={18} strokeWidth={2.4} />
                    <span>{t.launch} Game</span>
                  </button>
                  <button
                    type="button"
                    onClick={onBack}
                    className="inline-flex items-center justify-center rounded-xl border border-line bg-surface hover:bg-canvas text-ink font-dash font-bold text-dash-sm px-5 py-3 transition-colors cursor-pointer"
                  >
                    {t.back}
                  </button>
                </div>
              </div>
            </div>
          )
        }
      </Surface>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────── D4 ── */

/**
 * 🔴 SIX MEASURES, AND THE ROW CARRIED FOUR.
 *
 * The four it had — children, played, sittings, top of class — are all COUNTS OF
 * ACTIVITY. Between them they can say a class is busy; not one of them can say
 * whether the busyness was any good. A week of twenty-seven children opening the
 * game, tapping twice and walking away renders identically to a week of real
 * work: same headcount, same sitting count, same leader. The two new tiles are
 * the two questions a teacher actually asks on a Monday — *how long were they
 * in it* and *were they getting it right* — and they are the two the row was
 * structurally unable to answer.
 *
 * 🔴 AND EVERY TILE THAT CAN MOVE NOW SAYS WHICH WAY IT MOVED. A number with no
 * comparison is true and unactionable; nobody holds last week's dashboard in
 * their head. The server sends both windows (`thisWeek`/`lastWeek`) so the
 * subtraction happens once, against one clock — see `overviewMetrics.ts` for
 * every case where the honest answer is to show no arrow at all.
 *
 * ⚠️ A COMPONENT RATHER THAN JSX INLINE IN `ClassOverviewScreen`, for the reason
 * `ClassActivityPanel` below is one: six tiles with three conditional captions
 * each is ~120 lines in the middle of a screen that also draws four panels and a
 * form. It takes the whole overview rather than eight props because the tiles
 * must all read the SAME window — eight props is eight chances for one tile to
 * be handed last week's number.
 */
function ClassStatRow({
  o,
  lang,
  onOpenStudents,
}: {
  o: ClassOverview;
  lang: Lang;
  onOpenStudents: () => void;
}) {
  const t = TEACHER_COPY[lang];
  const m = useMemo(() => metricsOf(o), [o]);

  /*
   * ⚠️ ONE TRANSLATOR FOR ALL THREE DELTAS, so a tile cannot invent its own
   * wording for "no change" or drop the sign. `unit` exists because a change in
   * accuracy is measured in PERCENTAGE POINTS and a change in sittings is
   * measured in sittings — printing "+6" under a percentage would read as
   * "6% better", which is a different and wrong claim.
   */
  const deltaFor = (
    change: Change | null,
    unit: "count" | "points",
  ): StatDelta | undefined => {
    if (change === null) return undefined;
    if (change.direction === "flat") {
      return { direction: "flat", label: t.noChangeVsLastWeek };
    }
    return {
      direction: change.direction,
      label:
        unit === "points"
          ? t.vsLastWeekPoints(change.amount)
          : t.vsLastWeek(change.amount),
    };
  };

  return (
    /*
     * 🔴 SIX COLUMNS OF `xs`, NOT THREE OF `md` — ONE STRIP, NOT TWO BANKS.
     *
     * Three columns was the right fix for the WRONG PROBLEM: it stopped 6 ÷ 4
     * leaving two orphans, and left the six measures spending 275px of a 768px
     * laptop screen. The chart directly under this row — *"how often this class
     * plays"* — is the question a teacher opens the screen with on a Monday, and
     * it started below the fold so that six boxes could be large.
     *
     * ⚠️ THE ROW IS A SUMMARY, WHICH IS WHY IT MAY BE THE QUIET THING. Nothing
     * here is dropped, reordered or abbreviated: same six metrics, same tones,
     * same captions carrying the same denominators, same door on Children. Only
     * the box around each is smaller — `size="xs"` is that step, and its note in
     * `DashUI` is why the label stops wrapping when the column narrows.
     *
     * ⚠️ AND IT IS STILL 2 / 3 / 6 BY WIDTH. Six across is `xl` only; a phone
     * gets two and a tablet three, because a sixth of a 360px screen is not a
     * tile, it is a sliver.
     */
    <StatGrid columns={6}>
      {/*
        🔴 STILL THE ONLY TILE WITH A HANDLER, AND THE REASON IS UNCHANGED BY THE
        ROW GROWING. "Children · 27" counts exactly what the Students screen
        lists, so a teacher who reads the number and wants the names is already
        asking for that screen. The other five summarise a week — there is no
        screen that is "average accuracy" — and giving them chevrons for symmetry
        would be five doors that open onto nothing.
      */}
      <StatCard
        label={t.children}
        value={o.childCount}
        caption={t.totalStudents}
        icon={<ChildrenIcon size={16} strokeWidth={2.4} />}
        tone="indigo"
        size="xs"
        onClick={onOpenStudents}
      />

      {/*
        🔴 A PRE-PAIRED FRACTION, WHICH IS WHAT `StatCard` ASKS ITS CALLERS FOR.
        "0 / 4" says what a bare 0 cannot, and the denominator is drawn down a
        size so the eye still lands on the figure that actually moved this week.

        ⚠️ THE SHARE IS IN THE CAPTION AND NOT IN THE NUMERAL. "77%" as the
        headline would be a percentage whose denominator the reader has to
        reconstruct; the fraction is the figure, and the percentage is the gloss.
      */}
      <StatCard
        label={t.activeThisWeek}
        value={
          <>
            {m.played}{" "}
            <span className="text-dash-lg font-bold text-muted">
              / {m.childCount}
            </span>
          </>
        }
        caption={
          m.playedShare === null ? t.studentsPlayed : t.ofClass(m.playedShare)
        }
        delta={deltaFor(m.playedChange, "count")}
        icon={<PlayIcon size={16} strokeWidth={2.4} />}
        tone="leaf"
        size="xs"
      />

      <StatCard
        label={t.sessionsThisWeek}
        value={m.sessions}
        caption={m.sessionsChange === null ? t.firstWeek : t.totalSessions}
        delta={deltaFor(m.sessionsChange, "count")}
        icon={<TrendUpIcon size={16} strokeWidth={2.4} />}
        tone="teal"
        size="xs"
      />

      {/*
        🔴 THE NEW ONE THAT CATCHES A CLASS NOBODY IS SUPERVISING. Sittings and
        headcount both look healthy when children open the game and drift off;
        a two-minute mean is the only figure on this row that shows it.

        ⚠️ THE CAPTION IS THE DENOMINATOR, AND IT IS NOT DECORATION. A tablet
        closed mid-round writes no end time, so the mean routinely covers fewer
        sittings than the class ran — "32m" that silently describes 12 of 83 is
        the kind of number that gets repeated at a parents' evening. When nothing
        finished at all the tile shows an em dash over the reason rather than a
        confident "0m", which would be a different and false claim.
      */}
      <StatCard
        label={t.avgPlayTime}
        value={m.avgMinutes === null ? "—" : `${m.avgMinutes}m`}
        caption={
          m.avgMinutes === null
            ? t.noTimedSittings
            : `${t.perSitting} · ${t.timedOf(m.timedSessions, m.sessions)}`
        }
        icon={<ClockIcon size={16} strokeWidth={2.4} />}
        tone="primary"
        size="xs"
      />

      {/*
        🔴 THE ONLY PERCENTAGE ON THE ROW, AND IT IS ALLOWED BECAUSE ITS
        DENOMINATOR IS PRINTED UNDERNEATH IT. OD-39 banned *"Coordination 81%"* —
        81 % of what? — not division itself. "180 right of 250 answered" is
        checkable; that is `StrengthsPanel`'s rule at class scale.

        ⚠️ AND IT REFUSES BELOW A FLOOR RATHER THAN DIVIDING ANYWAY. Three right
        out of four is not a 75 % class, and a figure that swings twenty points
        on a single tap is visibly different on every reload — see
        `ACCURACY_FLOOR`.
      */}
      <StatCard
        label={t.avgAccuracy}
        value={m.accuracy === null ? "—" : `${m.accuracy}%`}
        caption={
          m.accuracy === null
            ? t.tooFewAnswers(ACCURACY_FLOOR)
            : t.answeredOf(m.answers.correct, m.answers.attempts)
        }
        delta={deltaFor(m.accuracyChange, "points")}
        icon={<TargetIcon size={16} strokeWidth={2.4} />}
        tone="coral"
        size="xs"
      />

      {/*
        ⚠️ THE NUMERAL CARRIES THE COINS AND THE CAPTION CARRIES THE NAME, so a
        class nobody has played yet shows an em dash over the reason rather than
        a confident 0 over a blank line.
      */}
      <StatCard
        label={t.topOfClass}
        value={o.topOfClass ? o.topOfClass.coins : "—"}
        caption={o.topOfClass ? o.topOfClass.name : t.nobodyPlayedYet}
        icon={<StickerIcon size={16} strokeWidth={2.4} />}
        tone="sunshine"
        size="xs"
      />
    </StatGrid>
  );
}

export interface ClassOverviewScreenProps extends Base {
  classId: string;
  /**
   * 🔴 THE NAME TRAVELS WITH THE ID, AND IT DID NOT USED TO.
   *
   * `AppRouter` opened `StudentDetail` with `childName: ''` and a comment
   * admitting the gap: *"the name is not on the callback, so the detail screen
   * is opened without one rather than with a guess"* — the right call given the
   * callback, and the callback was the thing to fix. Every row that offers this
   * button already carries `name` in the payload the teacher is reading, so
   * passing it costs nothing and removes a blank heading from a child's own
   * page.
   */
  onOpenChild: (playerId: string, name: string) => void;
  /**
   * The Children tile's door to the Students screen.
   *
   * 🔴 A SECOND WAY IN, AND — UNLIKE THE SHORTCUTS BELOW — ONE THE SCREEN
   * ACTUALLY EARNS. The rule the note below states is that a panel button
   * duplicating a permanent sidebar item is dead weight; this is not that
   * button. The tile is a COUNT OF THE THING THE OTHER SCREEN LISTS, so reading
   * "4" and wanting the four names is one thought, and making the number itself
   * the door is shorter than making the teacher find "Students" in the rail
   * afterwards. Leaderboard and Reports have no such number on this screen.
   *
   * ⚠️ REQUIRED, NOT OPTIONAL. `StatCard` draws its chevron off the presence of
   * a handler, so an optional prop would mean a tile that silently stops
   * looking like a door whenever a caller forgets it.
   */
  onOpenStudents: () => void;
  /*
   * ⚠️ NO `onOpenLeaderboard`/`onOpenReports` — THE SIDEBAR IS THE ONLY WAY IN,
   * AND IT IS ALWAYS ON SCREEN. D4 used to carry shortcut buttons to D6 and D7;
   * the redesign gave the tier a permanent sidebar with both as nav items, so
   * the panel buttons went. Callbacks a caller wires to a dispatch the screen
   * never fires read as "this screen offers that", which it does not.
   */
}

/** *"Needs attention is the panel that earns the dashboard."* */
export function ClassOverviewScreen({
  classId,
  lang = "en",
  auth,
  onOpenChild,
  onOpenStudents,
}: ClassOverviewScreenProps) {
  const t = TEACHER_COPY[lang];
  const resource = useResource<ClassOverview>(
    (signal) => getClassOverview(classId, { ...auth, signal }),
    [classId],
    { isEmpty: (o) => o.childCount === 0 },
  );

  return (
    <section className="font-dash text-dash-sm text-ink">
      <div>
        <Surface
          resource={resource}
          lang={lang}
          emptyActionLabel={t.importRoster}
        >
          {(o) => (
            <div className="flex flex-col gap-6">
              {/*
                🔴 THE ROW IS `StatGrid`/`StatCard` NOW, AND ~170 LINES OF
                HAND-DRAWN TILE WENT WITH IT.

                These four were built here: a 40 px disc on a row of its own, a
                `min-h-[8.75rem]` floor, and a decorative SVG absolutely
                positioned in every corner. That is the exact card `StatCard`
                was rewritten away from — its own note records why, *"every tile
                spent a whole row on decoration and read bottom-up"* — so D4 was
                the last screen still paying ~50 px a tile for a layout the
                other tiers had already dropped. The shared card is shorter, and
                it is the same card the school and platform tiers draw, which is
                what makes a head teacher who also teaches read one product
                rather than two.

                ⚠️ THE ILLUSTRATIONS ARE GONE, NOT SHRUNK. They are what set the
                floor height, and the tinted disc already carries the
                colour-as-identity they were saying a second time.
              */}
              <ClassStatRow
                o={o}
                lang={lang}
                onOpenStudents={onOpenStudents}
              />

              {/*
                🔴 THE SERIES THE FOUR TILES ABOVE CANNOT MAKE. They answer
                *this week* as scalars — "1 of 4 played, 1 sitting" — which
                cannot distinguish a class that has never started from one that
                stopped a fortnight ago, and those need opposite actions from
                the teacher reading them. It sits directly under the row it
                completes rather than further down the page, because it is the
                same question at a different resolution.

                ⚠️ ITS OWN REQUEST AND ITS OWN FOUR STATES, like every other
                panel in this file (§8.4). It is deliberately NOT folded into
                `getClassOverview`: a chart is a heavier read than four counters
                and a slow month of sittings must not delay the tiles a teacher
                looks at first.
              */}
              <ClassActivityPanel classId={classId} lang={lang} auth={auth} />

              {/*
                🔴 *WHAT* THEY PLAYED, BESIDE *WHEN* THEY PLAYED. The panel above
                draws sittings per day and answers whether this class is playing
                at all; it cannot say whether the fortnight went into one game
                while four sat untouched. That is the same question at a second
                axis, and the two belong together — a teacher deciding what to
                run this morning reads both or neither.

                🔴 THE COPY FOR THIS HEADING HAS EXISTED SINCE THE PANEL DID AND
                NOTHING DREW IT. `gamesTitle` and `gamesHint` sat in `copy.ts`
                with no caller, while the only mount hardcoded `title: ""` — the
                same shape as the operator tier's shipped-but-uncalled reads.
                Here it cost less: the panel was on Reports, one click away,
                under a heading a teacher reaches for at an inspection rather
                than on a Tuesday.

                ⚠️ ITS OWN RESOURCE, FOR `ClassActivityPanel`'S REASON. A games
                read that 403s must not blank the attention lists below it, and
                a slow one must not hold up the tiles above.

                ⚠️ AND IT IS THE SAME COMPONENT REPORTS DRAWS, NOT A COPY. Two
                per-game tables in one tier is how a class total stops matching
                itself between two tabs — `architecture.md` §12.
              */}
              <ClassGamesPanel
                classId={classId}
                lang={lang}
                auth={auth}
                title={t.gamesTitle}
                hint={t.gamesHint}
                tileSize="sm"
              />

              {/*
                🔴 THE SAME FORTNIGHT ON THE AXIS THAT ACTUALLY MATTERS. The
                panel above says which GAMES the class opened; this says which
                SKILLS that added up to — and they are not the same list, because
                one game carries a motor core beside its content skill and two
                games can serve one skill. A teacher plans a week by skill.

                ⚠️ IT SITS AFTER THE GAMES PANEL AND BEFORE THE PRIORITY CARDS,
                which is the order of the questions: is the class playing → what
                are they playing → what is it teaching → who needs me. The three
                cards below are the only part of this screen that names
                individual children, and they stay last of the reads for that
                reason.
              */}
              <ClassSkillsPanel classId={classId} lang={lang} auth={auth} />

              {/*
                🔴 THREE PRIORITY CARDS, AND THERE WERE TWO.

                D4 shipped with "Needs attention" beside "Not started" and
                nothing else — so a class where twenty of twenty-seven children
                are doing fine was drawn as two lists of problems and no mention
                of the twenty. A teacher opening this every morning read their
                class as a queue of failures, which is both demoralising and
                inaccurate: the two lists together are usually a small minority
                of the register.

                "Going well" is the same data subtracted — every child on the
                roll who is on neither list — and it costs one derived count.
                It is deliberately NOT a list of names: a teacher does not need
                to act on it, and printing twenty names would make the quiet
                majority louder than the seven who need chasing. The number is
                the whole message.

                ⚠️ THE ARITHMETIC IS A FLOOR, NOT A CLAIM. `childCount` is the
                register and the two attention lists are computed over children
                with recorded activity, so a child in neither could in principle
                be missing from both for a reason other than doing well. The
                caption says "not on either list" rather than "doing well"
                for that reason — the heading is the friendly word, the caption
                is the honest definition.

                ⚠️ AND `Math.max(0, …)` GUARDS THE SUBTRACTION. If the two lists
                ever overlap or outrun the register — different tables, and I5
                lets a sitting run unrecorded — the tile must read 0 rather than
                a negative number.
              */}
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <PriorityCard
                  title={t.needsAttention}
                  count={o.attention.needsAttention.length}
                  tone="attention"
                  icon={<AlertIcon size={16} />}
                  action={{
                    /* 🔴 IT GOES SOMEWHERE NOW. Both of these buttons read
                       "View all (4) ∨" and carried `onClick={() => {}}` — a
                       control that looks live and does nothing, which costs a
                       teacher a click to learn it is dead and teaches them not
                       to trust the next one. Students is the screen that lists
                       every child, which is what "view all" meant. */
                    label: t.openStudents,
                    onClick: onOpenStudents,
                  }}
                >
                  {o.attention.needsAttention.length === 0 ? (
                    <p className="py-6 text-center font-dash text-dash-sm font-bold text-muted">
                      {t.nobodyNeedsAttention}
                    </p>
                  ) : (
                    <ul className="flex flex-col gap-3">
                      {o.attention.needsAttention.map((row) => (
                        <li
                          key={row.playerId}
                          className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-line bg-canvas p-3 transition-colors duration-150 ease-dash hover:bg-surface"
                        >
                          <div className="flex min-w-0 items-center gap-2.5">
                            <Avatar name={row.name} size={32} />
                            <span className="tabular shrink-0 rounded-pill bg-primary-tint px-2 py-0.5 font-dash text-dash-xs font-extrabold text-primary">
                              {row.rollNumber ?? "—"}
                            </span>
                            <span className="truncate font-dash text-dash-sm font-bold text-ink">
                              {row.name}
                            </span>
                          </div>

                          <div className="flex shrink-0 items-center gap-2">
                            {/* ⚠️ THE REASON CHIPS WERE `bg-[#fffbeb]`,
                                `text-[#b45309]`, `border-[#fef3c7]`. The
                                `warning` pill tokens exist for exactly this
                                and are what `StatusPill` already uses. */}
                            {row.reasons.map((reason) => (
                              <span
                                key={reason}
                                className="inline-flex items-center gap-1 rounded-pill border border-pill-warning-line bg-pill-warning-bg px-3 py-1 font-dash text-dash-xs font-bold text-pill-warning-fg"
                              >
                                <AlertIcon size={12} />
                                {reason === "IDLE"
                                  ? t.idle(row.daysIdle ?? 0)
                                  : t.lowAccuracy(
                                      Math.round((row.accuracy ?? 0) * 100),
                                    )}
                              </span>
                            ))}
                            <button
                              type="button"
                              onClick={() => {
                                onOpenChild(row.playerId, row.name);
                              }}
                              className="flex items-center gap-1 rounded-pill border border-primary px-3.5 py-1 font-dash text-dash-xs font-bold text-primary transition-colors duration-150 ease-dash hover:bg-primary-tint"
                            >
                              {t.openChild} <ChevronIcon size={12} />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </PriorityCard>

                <PriorityCard
                  title={t.notStarted}
                  count={o.attention.notStarted.length}
                  tone="waiting"
                  icon={<PlayIcon size={16} />}
                  action={{ label: t.openStudents, onClick: onOpenStudents }}
                >
                  {o.attention.notStarted.length === 0 ? (
                    <p className="py-6 text-center font-dash text-dash-sm font-bold text-muted">
                      {t.everyoneStarted}
                    </p>
                  ) : (
                    <ul className="flex flex-col gap-3">
                      {o.attention.notStarted.map((row) => (
                        <li
                          key={row.playerId}
                          className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-line bg-canvas p-3 transition-colors duration-150 ease-dash hover:bg-surface"
                        >
                          <div className="flex min-w-0 items-center gap-2.5">
                            <Avatar name={row.name} size={32} />
                            <span className="tabular shrink-0 rounded-pill bg-canvas px-2 py-0.5 font-dash text-dash-xs font-extrabold text-muted">
                              {row.rollNumber ?? "—"}
                            </span>
                            <span className="truncate font-dash text-dash-sm font-bold text-ink">
                              {row.name}
                            </span>
                          </div>

                          <button
                            type="button"
                            onClick={() => {
                              onOpenChild(row.playerId, row.name);
                            }}
                            className="flex shrink-0 items-center gap-1 rounded-pill border border-indigo px-3.5 py-1 font-dash text-dash-xs font-bold text-indigo transition-colors duration-150 ease-dash hover:bg-canvas"
                          >
                            {t.openChild} <PlayIcon size={12} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </PriorityCard>

                <PriorityCard
                  title={t.goingWell}
                  count={Math.max(
                    0,
                    o.childCount -
                      o.attention.needsAttention.length -
                      o.attention.notStarted.length,
                  )}
                  tone="good"
                  icon={<CheckIcon size={16} />}
                  hint={t.goingWellHint}
                >
                  <p className="py-6 text-center font-dash text-dash-sm font-bold text-muted">
                    {t.goingWellBody(o.childCount)}
                  </p>
                </PriorityCard>
              </div>

              {/*
                🔴 THE REGISTER, ON THE SCREEN A TEACHER OPENS EVERY MORNING.
                Until now the only way to create a child was `POST
                /roster/import` — ADMIN-only against threat T8, because it moves
                hundreds of parent phone numbers behind one audit row. So a
                teacher who gained a child in October had to find an admin, who
                had to re-export a spreadsheet, to write down a name; and the
                register drifted from the room while every screen above this one
                quietly reported on the wrong set of children.

                ⚠️ LAST ON THE PAGE, AND IT IS THE ONLY THING HERE THAT WRITES.
                Everything above reads.

                ✅ AND IT NOW CARRIES THE FAMILY'S DETAILS TOO (ADR-050). It used
                to draw the child's name and roll alone, because a parent's phone
                number demanded `write` — an admin. That was the same mistake the
                permission itself had already corrected once: the teacher is the
                person the parent hands the number to at the gate, and making her
                find an admin to type it is what kept the register drifting from
                the room. `/roster/import` is still ADMIN-only against threat T8;
                one child at a time is a different act from hundreds behind one
                audit row.
              */}
              <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card">
                <h2 className="mb-1 font-dash text-dash-md font-extrabold text-ink">
                  {STUDENT_COPY[lang].addTitle}
                </h2>
                <p className="mb-4 text-dash-xs text-muted">
                  {STUDENT_COPY[lang].addWhy}
                </p>
                <AddStudentForm
                  classId={classId}
                  lang={lang}
                  auth={auth}
                  withParent
                  onAdded={resource.retry}
                />
              </div>
            </div>
          )}
        </Surface>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────── D5 ── */

export interface StudentDetailProps extends Base {
  playerId: string;
  childName: string;
  onExport: () => void;
}

export function StudentDetail({
  playerId,
  childName,
  lang = "en",
  auth,
  onExport,
}: StudentDetailProps) {
  const t = TEACHER_COPY[lang];
  const insight = useResource<Insight>(
    (signal) => getInsight(playerId, { ...auth, signal }),
    [playerId],
  );
  const events = useResource<EventRow[]>(
    (signal) => getPlayerEvents(playerId, { ...auth, signal }),
    [playerId],
    { isEmpty: (rows) => rows.length === 0 },
  );

  return (
    <section className="font-dash text-dash-sm text-ink">
      <PageHead
        title={childName === "" ? t.detail : childName}
        subtitle={childName === "" ? undefined : t.detail}
        actions={
          <DashButton variant="secondary" onClick={onExport}>
            {t.exportCsv}
          </DashButton>
        }
      />

      <SectionHead>{t.insight}</SectionHead>
      <Surface resource={insight} lang={lang} skeletonRows={3}>
        {(i) => (
          <Panel>
            <p className="text-dash-md text-ink">{i.summary}</p>
            {/*
              ⚠️ STRENGTHS AND FOCUS AS CHIPS, NOT COMMA-JOINED PROSE. They were
              rendered as `Strengths: a, b, c` — a sentence a teacher has to read
              to the end. Chips are scannable, and they let the two lists differ
              in weight without differing in colour alone (§2).
            */}
            {i.strengths.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="text-dash-xs font-bold text-muted">
                  {t.strengths}
                </span>
                {i.strengths.map((s) => (
                  <StatusPill
                    key={s}
                    tone="success"
                    icon={<CheckIcon size={14} />}
                  >
                    {s}
                  </StatusPill>
                ))}
              </div>
            )}
            {i.focusAreas.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="text-dash-xs font-bold text-muted">
                  {t.focus}
                </span>
                {i.focusAreas.map((s) => (
                  <StatusPill
                    key={s}
                    tone="warning"
                    icon={<TargetIcon size={14} />}
                  >
                    {s}
                  </StatusPill>
                ))}
              </div>
            )}
          </Panel>
        )}
      </Surface>

      {/* 🔴 DISABLED WITH ITS REASON, NOT HIDDEN. WhatsAppClient is
          NotImplemented until Unit 9. A teacher who cannot find the feature
          files a ticket; one who can see why it is greyed out does not. */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <DashButton
          variant="secondary"
          disabled
          title={t.messageParentDisabled}
        >
          {t.messageParent}
        </DashButton>
        <span className="text-dash-xs text-muted">
          {t.messageParentDisabled}
        </span>
      </div>

      <SectionHead>{t.recentEvents}</SectionHead>
      <Surface resource={events} lang={lang} skeletonRows={5}>
        {(rows) => <PaginatedEventsTable rows={rows} t={t} />}
      </Surface>
    </section>
  );
}

function PaginatedEventsTable({ rows, t }: { rows: EventRow[]; t: (typeof TEACHER_COPY)[Lang] }) {
  const { paginatedItems, page, totalPages, setPage, totalItems } = usePagination(rows, 7);

  return (
    <>
      <TableCard>
        <table className="w-full border-collapse">
          <Thead headers={[t.when, t.game, t.event, t.skill]} />
          <tbody>
            {paginatedItems.map((e) => (
              <Tr key={e.id}>
                <Td num soft>
                  {new Date(e.createdAt).toLocaleString()}
                </Td>
                <Td>{e.game}</Td>
                <Td soft>{e.type}</Td>
                <Td soft>
                  {e.skill ?? "—"}
                  {e.level ? ` L${e.level}` : ""}
                </Td>
              </Tr>
            ))}
          </tbody>
        </table>
      </TableCard>
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalItems={totalItems}
      />
    </>
  );
}

/* ─────────────────────────────────────────────────────────────── D6 ── */

export interface LeaderboardScreenProps extends Base {
  classId: string;
}

export function LeaderboardScreen({
  classId,
  lang = "en",
  auth,
}: LeaderboardScreenProps) {
  const t = TEACHER_COPY[lang];
  const resource = useResource<LeaderboardRow[]>(
    (signal) => getLeaderboard(classId, { ...auth, signal }),
    [classId],
    { isEmpty: (rows) => rows.length === 0 },
  );

  return (
    <section className="font-dash text-dash-sm text-ink max-w-5xl mx-auto pb-12">
      {/* ── Page Header ── */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-line bg-primary-tint text-primary shadow-sm">
            <MedalIcon size={22} strokeWidth={2.4} />
          </div>
          <div>
            <h1 className="font-dash text-2xl font-extrabold text-ink">
              {t.leaderboard}
            </h1>
            <p className="mt-0.5 font-dash text-dash-xs font-bold text-muted">
              {t.leaderboardHint}
            </p>
          </div>
        </div>

        {/*
          ✅ THE 🏆✨ BADGE IS GONE ENTIRELY RATHER THAN REDRAWN.
          It was a 96×64 box in the top-right corner holding two emoji, on
          `bg-[#eff6ff] border-blue-100` — neither a token — and it said
          nothing the heading beside it did not already say. Replacing it with
          an icon would have been the same decoration in a better typeface;
          `design-system.md` §12 lists decoration that carries no fact on the
          Never list. The row is quieter and one fewer thing competes with the
          four tiles below it.
        */}
      </div>

      <Surface resource={resource} lang={lang} skeletonRows={6}>
        {(rows) => {
          const totalCoins = rows.reduce((acc, r) => acc + (r.coins || 0), 0);
          const totalStickers = rows.reduce(
            (acc, r) => acc + (r.stickers || 0),
            0,
          );
          const bestStreak =
            rows.length > 0
              ? Math.max(...rows.map((r) => r.streakDays || 0))
              : 0;
          /*
           * 🔴 COUNTED, NOT ASSUMED. This tile used to render
           * `{rows.length} / {rows.length}` over the fixed words "100%
           * participation" — a class where nobody had ever opened a game still
           * read as everybody active, which is the one number on this screen a
           * teacher would act on. A child has played when the server says when.
           */
          const activeCount = rows.filter((r) => r.lastPlayed !== null).length;
          const participation =
            rows.length > 0
              ? Math.round((activeCount / rows.length) * 100)
              : 0;

          return (
            <>
              {/*
                🔴 FOUR HAND-BUILT CARDS BECOME THE KIT'S ONE. Each was a
                `rounded-2xl border border-amber-200 bg-gradient-to-br
                from-[#fffbeb] to-[#fef3c7]` written out in full — six hardcoded
                colours per tile, times four — beside the same three figures the
                student page drew as plain `StatCard`s. Same numbers, same words,
                two designs, one sidebar apart.

                ⚠️ AND THE EMOJI GO WITH THEM. These carried 🪙 ⭐ 🔥 👥 twice
                each, in the disc and again as the watermark;
                `design-system.md` §6 reserves emoji for game identity and §12
                lists "emoji as a UI icon" on the Never list, which
                `scripts/check-emoji.mjs` exists to enforce. They are also a
                font-stack lottery on the school laptops this is read on.
              */}
              <div className="mb-8">
                <StatGrid>
                  <RewardTile
                    label={t.leaderboardTotalCoins}
                    value={totalCoins}
                    caption={t.leaderboardCoinsCaption}
                    icon={<PointIcon size={18} />}
                    tone="sunshine"
                  />
                  <RewardTile
                    label={t.leaderboardTotalStickers}
                    value={totalStickers}
                    caption={t.leaderboardStickersCaption}
                    icon={<StickerIcon size={18} />}
                    tone="primary"
                  />
                  <RewardTile
                    label={t.leaderboardBestStreak}
                    value={bestStreak}
                    caption={t.leaderboardStreakCaption}
                    icon={<TrendUpIcon size={18} />}
                    tone="leaf"
                  />
                  {/* 🔴 A PAIR, NEVER A BARE PERCENTAGE (OD-39/OD-72) — and the
                      share stays in the caption where it is read as context
                      rather than as the figure itself. */}
                  <RewardTile
                    label={t.leaderboardActiveStudents}
                    value={`${String(activeCount)} / ${String(rows.length)}`}
                    caption={t.leaderboardActiveCaption(participation)}
                    icon={<ChildrenIcon size={18} />}
                    tone="teal"
                  />
                </StatGrid>
              </div>

              {/* ── Leaderboard Table Card ── */}
              <PaginatedLeaderboardTable rows={rows} t={t} lang={lang} />

              {/*
                ✅ THE "KEEP ENCOURAGING!" BANNER IS GONE, AND IT IS THE ONE
                DELETION ON THIS SCREEN WORTH ARGUING FOR.

                It was a teal gradient strip carrying a ⚙️ in a disc — a COG,
                beside the words "Keep encouraging!", which is not what a cog
                means in any interface — plus ⭐🌈 in the corner and two
                sentences of encouragement addressed to the teacher. Six
                hardcoded colours (`#ccfbf1`, `#f0fdf4`, `#e6fffa`, `#0f766e`
                twice, `white`), none of them tokens.

                It also stated no fact. §12's rule is that every element on a
                data screen carries something the reader can act on, and a
                banner telling a teacher that coins are "a step towards
                learning and growing" is the product congratulating itself in
                the space where the next class's numbers could be. The four
                tiles and the table above already say everything this screen
                knows.
              */}
            </>
          );
        }}
      </Surface>
    </section>
  );
}

function PaginatedLeaderboardTable({
  rows,
  t,
  lang,
}: {
  rows: LeaderboardRow[];
  t: (typeof TEACHER_COPY)[Lang];
  /** 🔴 Needed for SKILL_LABEL — the enum must never reach a teacher raw. */
  lang: Lang;
}) {
  const { paginatedItems, page, totalPages, setPage, totalItems } = usePagination(rows, 7);

  return (
    <>
      <div className="rounded-2xl border border-line bg-surface overflow-hidden shadow-card mb-4">
        <div className="scroll-x" tabIndex={0}>
        <table className="w-full border-collapse text-left font-dash text-dash-xs">
          <thead>
            <tr className="bg-primary-tint border-b border-line text-ink font-extrabold">
              <th className="px-5 py-3.5">{t.roll}</th>
              <th className="px-5 py-3.5">{t.student}</th>
              {/*
                🔴 THE GLYPH IS AN ICON AND IT SITS IN THE HEADER ONLY.
                These four columns carried 🪙 ⭐ 🔥 📈 in the header AND again
                in every one of thirty rows — the same symbol printed 31 times
                per column to say what the header already said once. The icons
                stay in the header, where they help a reader find the column;
                the cells are numbers, which is what a teacher reads down.
              */}
              <th className="px-5 py-3.5">
                <span className="flex items-center gap-1.5">
                  <CoinIcon size={14} /> {t.coins}
                </span>
              </th>
              <th className="px-5 py-3.5">
                <span className="flex items-center gap-1.5">
                  <StickerIcon size={14} /> {t.stickers}
                </span>
              </th>
              <th className="px-5 py-3.5">
                <span className="flex items-center gap-1.5">
                  <FlameIcon size={14} /> {t.streak}
                </span>
              </th>
              <th className="px-5 py-3.5">
                <span className="flex items-center gap-1.5">
                  <TrendUpIcon size={14} /> {t.topSkill}
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {paginatedItems.map((r, idx) => {
              const rank = (page - 1) * 7 + idx + 1;
              return (
                <tr
                  key={r.playerId}
                  className="hover:bg-canvas/50 transition-colors"
                >
                  {/*
                    🔴 `RankBadge`, AND THE NUMERAL IS WHY. The three places
                    were 🥇 🥈 🥉, which carry no text at all — §12's rule is
                    that status is icon PLUS colour PLUS text, and a medal emoji
                    is the one carrier doing all three jobs badly. Every row now
                    prints its rank as a number; the top three additionally get
                    a medal outline and a tone.

                    ⚠️ IT ALSO DROPS SIX MORE RAW HEX VALUES — `#fef3c7`,
                    `#d97706`, `#f1f5f9`, `#64748b`, `#ffedd5`, `#c2410c` — plus
                    `border-slate-300`, which is a GREY, and this token set has
                    no grey in it by rule.
                  */}
                  <td className="px-5 py-4 font-extrabold">
                    <RankBadge rank={rank} />
                  </td>

                  <td className="flex items-center gap-3 px-5 py-4 font-extrabold text-ink">
                    {/* ⚠️ `Avatar`, NOT `r.name.charAt(0)`. One initial
                        collides constantly in a class — two Aaravs are the
                        same disc — and the shared component takes the first
                        letter of the first and last word instead. */}
                    <Avatar name={r.name} size={32} />
                    <span>{r.name}</span>
                  </td>

                  <td className="tabular px-5 py-4 font-extrabold text-ink">
                    {r.coins}
                  </td>

                  <td className="tabular px-5 py-4 font-extrabold text-ink">
                    {r.stickers}
                  </td>

                  <td className="tabular px-5 py-4 font-extrabold text-ink">
                    {r.streakDays}
                  </td>

                  {/*
                    🔴 THIS CHIP WAS COMPARING AGAINST STRINGS THE SERVER NEVER
                    SENDS, AND IT PRINTED THE DATABASE'S WORDS AT A TEACHER.

                    `LeaderboardRow.topSkill` is `ContentSkill | null` — the
                    enum, so the values are `NUMERACY`, `LITERACY`,
                    `GENERAL_AWARENESS`, `SOCIAL_AWARENESS`, `MATCHING`. The
                    branch read `r.topSkill === 'Numeracy'` and
                    `=== 'General Awareness'`, which match none of them. So the
                    first two arms were unreachable, every row in every class
                    fell through to the green arm, and the cell rendered
                    `GENERAL_AWARENESS` in capitals with an underscore in it.

                    Both halves are fixed by using the tables that already
                    exist: `SKILL_LABEL` turns the enum into "The world around
                    us" — the same words the student page and the school tier
                    print — and `skillTint` gives the skill its fixed colour
                    from `--color-skill-*`. §3 sets that map once precisely so a
                    skill is the same colour everywhere, which five hardcoded
                    hex values could not be.

                    ⚠️ THE COLOUR IS PER SKILL, NOT PER RANK. It is identity
                    (§12) — a teacher recognises "matching" by its indigo across
                    every screen — and says nothing about how the child is
                    doing.
                  */}
                  <td className="px-5 py-4">
                    {r.topSkill ? (
                      <span
                        className="inline-flex items-center gap-1.5 rounded-pill border px-3 py-1 font-dash text-dash-xs font-bold"
                        style={{
                          background: `color-mix(in srgb, ${skillTint(r.topSkill as Skill)} 13%, var(--color-surface))`,
                          borderColor: `color-mix(in srgb, ${skillTint(r.topSkill as Skill)} 30%, var(--color-line))`,
                          color: `color-mix(in srgb, ${skillTint(r.topSkill as Skill)} 68%, var(--color-ink))`,
                        }}
                      >
                        {SKILL_LABEL[r.topSkill]?.[lang] ?? r.topSkill}
                      </span>
                    ) : (
                      <span className="font-bold text-muted">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalItems={totalItems}
      />
    </>
  );
}

/* ─────────────────────────────────────────────────────────────── D7 ── */

export interface ReportsScreenProps extends Base {
  classId: string;
  /*
   * 🔴 IT RETURNS A PROMISE NOW, AND THAT IS THE WHOLE OF THE FIX ON THIS SIDE.
   * The prop was `() => void`, so this screen could not know whether the export
   * had started, finished, or failed — and the handler behind it did nothing at
   * all. A button that cannot report an outcome is a button that reports success
   * for every outcome.
   */
  onExportCsv: () => Promise<void>;
}

/**
 * ⚠️ COVERAGE, NOT A REPORT CARD. The TODO asks for *"NCF-tagged coverage,
 * PDF/Excel, Holistic Progress Card mapping"*. Coverage and CSV are here; the
 * PDF and the HPC are not built, and OD-59 says why — the Holistic Progress
 * Card is a specific Government of India artefact and no document in this repo
 * defines its fields. A guessed layout is a document a school might file.
 */
/*
 * ✅ `GAME_ICONS` IS GONE, AND IT WAS A SECOND SET OF GAME EMOJI THAT
 * DISAGREED WITH THE FIRST.
 *
 * It read `'sky-quest': '🐦'`, `'letter-lagoon': '🐸'`, `'kalqy-world': '✋'`.
 * The canonical identity emoji live in `shared/curriculum/*.ts` and are 🔢, 🔤,
 * 🌍, 🤝 and 🧩 — §6 permits exactly those, and the curriculum files are on
 * `check-emoji`'s allowlist for exactly that reason. So a teacher's report
 * showed a frog for the game the child had just played as a letter, and the
 * same game was two different pictures one nav item apart.
 *
 * The replacement is not a corrected emoji table — it is `GAME_ART_BY_ID`,
 * which the school tier and the parent's report already draw from. Real
 * illustrations, the game's title as a parent would say it, and the skill's
 * fixed tint from `--color-skill-*`. Its own header states the rule this file
 * broke: *"derived, never a second table — two hand-written maps keyed
 * differently are two maps that disagree the first time a game is renamed."*
 *
 * ✅ `CATEGORY_STYLES` GOES WITH IT, FOR THE SAME REASON ONE LEVEL ALONG. It
 * mapped five category DISPLAY strings to ten hardcoded hex values, so it broke
 * silently whenever a category was reworded, and it duplicated the skill→colour
 * map §3 fixes once. `GameArt.tint` is that map.
 */



/**
 * What this class has actually been playing.
 *
 * 🔴 THE PANEL IS SHARED AND THE TITLE IS NOT. `GamesPanel` draws every number
 * on every tier; the heading is the one thing that genuinely differs — a teacher
 * reads *"this class"*, a head teacher reads *"this school"*, an operator reads
 * *"every school, last 30 days"*. So the tier supplies two strings and spreads
 * the rest of `GAMES_PANEL_COPY`, which is why there is exactly one dictionary
 * for the columns.
 *
 * ⚠️ THE CHILD COUNT COMES FROM THE SAME RESPONSE AS THE ROWS. Reading it from a
 * separate roster call would be two numbers that disagree the moment a child is
 * added mid-render — and the whole reason `children` is a count rather than a
 * percentage is that the denominator has to arrive with it.
 */
function ClassGamesPanel({
  classId,
  lang,
  auth,
  title = "",
  hint,
  tileSize = "md",
}: {
  classId: string;
  lang: Lang;
  auth?: StaffAuth;
  /**
   * 🔴 THE HEADING IS THE CALLER'S, WHICH IS WHAT THIS FILE'S OWN HEADER
   * ALREADY SAID AND THE CODE DID NOT DO. It read *"the panel is shared and the
   * title is not"* and then hardcoded `title: ""` — so the one string it names
   * as the tier's to choose was the one string nobody could choose.
   *
   * ⚠️ IT DEFAULTS TO EMPTY BECAUSE REPORTS DRAWS ITS OWN HEADING ABOVE THIS
   * PANEL. Two headings stacked is worse than none, and `GamesPanel` skips its
   * `SectionHead` entirely on an empty title.
   */
  title?: string;
  hint?: string;
  /**
   * How loud the panel's four summary tiles are — `GamesPanel`'s own prop,
   * forwarded because the two mounts in this file want opposite answers.
   *
   * ⚠️ REPORTS KEEPS `md` AND THE OVERVIEW TAKES `sm`. On Reports this panel is
   * the page; on the overview it is the third thing down, under a strip of six
   * measures the teacher reads first, and a tile larger than the row above it
   * would say the games total is the more important number.
   */
  tileSize?: "md" | "sm";
}) {
  const t = TEACHER_COPY[lang];
  const resource = useResource<ClassGames>(
    (signal) => getClassGames(classId, { ...auth, signal }),
    ["class-games", classId],
  );

  return (
    <DataSurface
      state={resource.state}
      skeletonRows={5}
      errorMessage={describeError(resource.error, lang)}
      errorRetryLabel={t.retry}
      onRetry={resource.retry}
    >
      {resource.data === null ? null : (
        <GamesPanel
          games={resource.data.games}
          summary={resource.data.summary}
          childCount={resource.data.childCount}
          lang={lang}
          tileSize={tileSize}
          copy={{
            ...GAMES_PANEL_COPY[lang],
            title,
            hint,
          }}
        />
      )}
    </DataSurface>
  );
}

export function ReportsScreen({
  classId,
  lang = "en",
  auth,
  onExportCsv,
}: ReportsScreenProps) {
  const t = TEACHER_COPY[lang];
  const resource = useResource<NcfCoverage>(
    (signal) => getCoverage(classId, { ...auth, signal }),
    [classId],
  );

  /*
   * ⚠️ THREE STATES, NOT TWO. `idle` and `busy` are the download; `failed` is
   * the one the old button could never reach, because it never issued a request.
   * The failure is drawn beside the button that caused it rather than as a
   * page-level banner — D3's launch error established that, and it is the same
   * reason: the teacher is looking at the control she just pressed.
   */
  const [exportState, setExportState] = useState<"idle" | "busy" | "failed">(
    "idle",
  );

  const runExport = async () => {
    setExportState("busy");
    try {
      await onExportCsv();
      setExportState("idle");
    } catch {
      setExportState("failed");
    }
  };

  return (
    <section className="font-dash text-dash-sm text-ink max-w-5xl mx-auto pb-12">
      {/* ── Header ── */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          {/* ⚠️ `ReportsIcon`, not 📌. A pin says "saved for later"; this
              screen is the curriculum coverage record, which is what the
              icon set's `reports` glyph is for. The disc was also
              `bg-[#f3e8ff] text-[#9333ea]` — neither a token. */}
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-line bg-primary-tint text-primary shadow-sm">
            <ReportsIcon size={18} strokeWidth={2.4} />
          </div>
          <div>
            <h1 className="font-dash text-2xl font-extrabold text-ink">
              {t.reports}
            </h1>
            <p className="mt-0.5 font-dash text-dash-xs font-bold text-muted">
              {t.reportsHint}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <button
            type="button"
            onClick={() => void runExport()}
            disabled={exportState === "busy"}
            className="rounded-full bg-primary text-white px-5 py-2 font-dash text-dash-xs font-bold shadow-sm transition-colors hover:bg-primary-deep flex items-center gap-2 cursor-pointer disabled:cursor-wait disabled:opacity-70"
          >
            <DownloadIcon size={16} />
            <span>
              {exportState === "busy" ? t.exportCsvBusy : t.exportCsv}
            </span>
          </button>
          {exportState === "failed" && (
            <p
              role="alert"
              className="max-w-[16rem] text-right font-dash text-dash-xs font-bold text-danger"
            >
              {t.exportCsvFailed}
            </p>
          )}
        </div>
      </div>

      {/*
        🔴 WHAT THEY ARE PLAYING SITS ABOVE WHAT IT COVERS, AND THE ORDER IS THE
        POINT. Coverage answers the inspector's question — which NCF-FS
        competencies has this class evidenced, where one event counts. This
        answers the teacher's: rounds, right answers, the level reached, when it
        was last opened. She is here on a Tuesday, not at an inspection, and the
        panel she can act on this week goes first.

        ⚠️ ITS OWN RESOURCE, SO THE TWO FAIL INDEPENDENTLY. A coverage read that
        403s says nothing about what the class has played, and a screen that lost
        both to one error would be a screen that reports a scoping problem as an
        empty curriculum.
      */}
      <div className="mb-8">
        {/*
          🔴 LABELLED HERE TOO, AND ON THIS SCREEN IT MATTERS MORE. The page
          heading above says *"which NCF-FS codes this class has covered"*, and
          this table is not that — it is rounds, accuracy and levels. An
          unlabelled table under a heading describing something else reads as
          part of it, so a teacher could take a game with four rounds against it
          as a competency evidenced four times.

          ⚠️ `gamesHint` WAS WRITTEN FOR EXACTLY THIS AND NEVER RENDERED. Its own
          note in `copy.ts` says so: *"it is not coverage, and the hint says so
          in one line."* The line existed; the panel was passed an empty title,
          which suppresses the whole heading including the hint.
        */}
        <ClassGamesPanel
          classId={classId}
          lang={lang}
          auth={auth}
          title={t.gamesTitle}
          hint={t.gamesHint}
        />
      </div>

      <Surface resource={resource} lang={lang} skeletonRows={5}>
        {(c) => (
          <>
            {/* ── By Game Table Card ── */}
            <div className="mb-8">
              <h2 className="font-dash text-dash-md font-extrabold text-ink flex items-center gap-2 mb-3">
                <GamepadIcon size={18} /> {t.byGame}
              </h2>

              <div className="rounded-2xl border border-line bg-surface overflow-hidden shadow-card">
                <div className="scroll-x" tabIndex={0}>
                <table className="w-full border-collapse text-left font-dash text-dash-xs">
                  <thead>
                    <tr className="bg-[#f5f3ff] border-b border-line text-ink font-extrabold">
                      <th className="px-5 py-3.5">{t.game}</th>
                      <th className="px-5 py-3.5">{t.category}</th>
                      <th className="px-5 py-3.5">{t.codes}</th>
                      <th className="px-5 py-3.5">{t.events}</th>
                      <th className="px-5 py-3.5">{t.children}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {c.rows.map((r) => (
                      <tr
                        key={r.game}
                        className="hover:bg-canvas/50 transition-colors"
                      >
                        {/*
                          🔴 THE GAME'S OWN PLATE AND ITS OWN NAME.
                          This drew an emoji disc beside `r.game`, which is the
                          raw id the events table records — so the cell read
                          "🐦 sky-quest" at a teacher. `GAME_ART_BY_ID` carries
                          the illustration a child sees and the title a parent
                          would say, and falls back to the id only for a game
                          this build does not know.
                        */}
                        <td className="flex items-center gap-2.5 px-5 py-4 font-extrabold text-ink">
                          {(() => {
                            const art = GAME_ART_BY_ID[r.game as GameId] as
                              | GameArt
                              | undefined;
                            return (
                              <>
                                <span
                                  className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border"
                                  style={{
                                    borderColor: art
                                      ? `color-mix(in srgb, ${art.tint} 32%, var(--color-line))`
                                      : "var(--color-line)",
                                    background: art
                                      ? `color-mix(in srgb, ${art.tint} 12%, var(--color-surface))`
                                      : "var(--color-canvas)",
                                  }}
                                >
                                  {art ? (
                                    <img
                                      src={art.art}
                                      alt=""
                                      aria-hidden="true"
                                      className="h-full w-full object-cover"
                                    />
                                  ) : null}
                                </span>
                                <span>{art ? art.title[lang] : r.game}</span>
                              </>
                            );
                          })()}
                        </td>
                        {/* ⚠️ THE CATEGORY CHIP IS NEUTRAL NOW. It used to be
                            keyed off the category's DISPLAY string against ten
                            hardcoded hex values, so it silently lost its colour
                            the moment a category was reworded. The colour that
                            means something on this row is the game's tint,
                            which the cell to its left already carries. */}
                        <td className="px-5 py-4">
                          <span className="inline-flex items-center rounded-pill border border-line bg-canvas px-3 py-1 font-dash text-dash-xs font-bold text-ink">
                            {r.category}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex flex-wrap gap-1.5">
                            {r.codes.map((code) => (
                              <span
                                key={code}
                                className="rounded-md bg-canvas px-2 py-0.5 font-dash text-[11px] font-bold text-muted border border-line"
                              >
                                {code}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-5 py-4 font-bold text-ink">
                          <div className="flex flex-col">
                            <span>{r.events}</span>
                            <span className="text-[11px] font-normal text-muted">
                              activities
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-4 font-bold text-ink">
                          <div className="flex flex-col">
                            <span>{r.children}</span>
                            <span className="text-[11px] font-normal text-muted">
                              children
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          </>
        )}
      </Surface>
    </section>
  );
}

/**
 * D4's chart, as its own panel with its own request.
 *
 * ⚠️ A COMPONENT RATHER THAN JSX INLINE IN `ClassOverviewScreen`, because it
 * needs a `useResource` of its own and hooks cannot live inside the render
 * callback `Surface` invokes. That is the same reason `ChildGamesPanel` and
 * `StrengthsPanel` are components on the student page.
 */
/**
 * 🔴 *WHAT* THIS CLASS IS LEARNING, BESIDE HOW MUCH IT PLAYED.
 *
 * Every tile in `ClassStatRow` counts activity, and `ClassActivityPanel` counts
 * it again over time. Between them a class that has spent a fortnight in Sky
 * Quest and touched nothing else reads as an exemplary class — busy, accurate,
 * improving — because none of those measures has an axis for *which skill*. This
 * is that axis, and it is the one a teacher plans next week on.
 *
 * ⚠️ SHARES OF THE REGISTER, NOT EVENT COUNTS. `ClassSkillRow` carries both, and
 * `events` is the number that flatters: one child grinding Letter Lagoon
 * produces more literacy events than fifteen children doing a level each. "12 of
 * 27 children have touched this" is the fact a teacher can act on, so the bars
 * draw `children` and the count beside every bar spells the pair out — the chart
 * kit's own rule, *"a width cannot be read aloud at a governors' meeting"*.
 *
 * ⚠️ ITS OWN RESOURCE, FOR `ClassActivityPanel`'S REASON (§8.4). This is the
 * heaviest read on the screen — every event the class has ever produced, folded
 * five ways — and it must not delay the tiles or blank the attention lists when
 * it fails.
 */
function ClassSkillsPanel({
  classId,
  lang,
  auth,
}: {
  classId: string;
  lang: Lang;
  auth?: StaffAuth;
}) {
  const t = TEACHER_COPY[lang];
  const resource = useResource<ClassSkills>(
    (signal) => getClassSkills(classId, { ...auth, signal }),
    ["class-skills", classId],
  );

  /*
   * ⚠️ BOUND TO A LOCAL, WHICH IS HOW THE SCHOOL SCREEN DRAWS ITS OWN SKILL
   * PANEL. TypeScript drops the null-narrowing on a property access inside a
   * `.map` callback, and the alternative — a `!` — is the assertion that stops
   * being true the first time this resource can settle empty.
   */
  const skills = resource.data;
  /* 🔴 THE UNTOUCHED ONES, NAMED. A zero-width bar is indistinguishable from a
     rendering fault, and "nobody has opened Matching & Sorting" is the single
     most actionable line this panel produces. */
  const untouched = (skills?.rows ?? []).filter((row) => row.children === 0);

  return (
    <DataSurface
      state={resource.state}
      skeletonRows={5}
      errorMessage={describeError(resource.error, lang)}
      errorRetryLabel={t.retry}
      onRetry={resource.retry}
    >
      {skills === null ? null : (
        <ChartPanel
          title={t.skillsTitle}
          hint={t.skillsHint}
          /*
           * 🔴 THE READ'S OWN DENOMINATOR, IN THE HEADER RATHER THAN UNDER A
           * BAR. Every row below is drawn from the children who have played at
           * all — four of twenty-seven, on the first week of a pilot — and a
           * teacher reading five short bars needs to know that before she reads
           * them as five gaps in her teaching.
           */
          action={
            <span className="font-dash text-dash-xs font-bold text-muted">
              {t.skillsBasis(skills.childrenWithAnyEvents, skills.childCount)}
            </span>
          }
        >
          <ShareBars
            emptyMessage={t.skillsNothingYet}
            rows={skills.rows.map((row) => ({
              key: row.skill,
              /* 🔴 THE LABEL, NOT THE ENUM. `GENERAL_AWARENESS` is the
                 database's word; `SKILL_LABEL` is this tier's own table and is
                 already imported for the leaderboard chip. */
              label: SKILL_LABEL[row.skill]?.[lang] ?? row.skill,
              count: row.children,
              total: skills.childCount,
              /*
               * ⚠️ ONE TONE FOR ALL FIVE, NOT `skillTint` PER ROW. Every bar
               * here answers the same question, so a colour that changed per row
               * would be decoration carrying nothing (§2) — and five tints in one
               * chart is how a reader starts looking for a meaning that is not
               * there. The label says which skill it is.
               */
              tone: "teal" as const,
            }))}
          />
          {untouched.length === 0 ? null : (
            <p className="mt-4 border-t border-line pt-3 font-dash text-dash-xs font-bold text-muted">
              {t.skillUntouched}
              {": "}
              {untouched
                .map((row) => SKILL_LABEL[row.skill]?.[lang] ?? row.skill)
                .join(", ")}
            </p>
          )}
        </ChartPanel>
      )}
    </DataSurface>
  );
}

function ClassActivityPanel({
  classId,
  lang,
  auth,
}: {
  classId: string;
  lang: Lang;
  auth?: StaffAuth;
}) {
  const t = TEACHER_COPY[lang];
  /*
   * ⚠️ 30 DAYS, WHICH IS THE SERVER'S DEFAULT AND ITS CLAMP'S MIDDLE. Long
   * enough that a weekly rhythm is visible as a rhythm rather than as noise,
   * short enough that a class which changed habit last month is not averaged
   * into looking steady.
   */
  const days = 30;
  const resource = useResource<ClassActivity>(
    (signal) => getClassActivity(classId, days, { ...auth, signal }),
    ["class-activity", classId],
  );

  return (
    <div className="rounded-lg border border-line bg-surface p-3 shadow-card sm:p-4">
      <h2 className="font-dash text-dash-md font-extrabold text-ink">
        {t.activityTitle}
      </h2>
      <p className="mt-1 mb-4 font-dash text-dash-xs text-muted">
        {t.activityHint(days)}
      </p>
      <Surface resource={resource} lang={lang}>
        {(data) => (
          <ClassActivityChart
            points={data.points}
            copy={{
              sessions: t.activitySessions,
              children: t.activityChildren,
              nothingYet: t.activityNothingYet,
            }}
          />
        )}
      </Surface>
    </div>
  );
}
